import React, { useState } from 'react';
import { Customer } from '../types/types';
import { apiService } from '../services/api';
import '../styles/common.css';

interface EditCustomerPopupProps {
  customer: Customer;
  onClose: () => void;
  onSave: (apikey: string, changes: {
    readBatchSize?: number;
    readDelayMillis?: number;
    postURL?: string;
  }) => Promise<void>;
}

export const EditCustomerPopup: React.FC<EditCustomerPopupProps> = ({
  customer,
  onClose,
  onSave
}) => {
  const [changes, setChanges] = useState({
    readBatchSize: customer.readBatchSize || '',
    readDelayMillis: customer.readDelayMillis || '',
    postURL: customer.postURL || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof typeof changes) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // --- 1. Check for any difference ---
      const originalBatchSize = customer.readBatchSize ?? '';
      const originalDelay = customer.readDelayMillis ?? '';
      const originalPostURL = customer.postURL ?? '';

      const currentBatchSizeStr = String(changes.readBatchSize ?? '').trim();
      const currentDelayStr = String(changes.readDelayMillis ?? '').trim();
      const currentPostURLStr = String(changes.postURL ?? '').trim();

      const hasAnyDifference =
        currentBatchSizeStr !== String(originalBatchSize) ||
        currentDelayStr !== String(originalDelay) ||
        currentPostURLStr !== String(originalPostURL);

      // --- 2. If no difference, close ---
      if (!hasAnyDifference) {
        console.log("No changes detected.");
        onClose();
        setIsSaving(false); // Ensure saving state is reset
        return;
      }

      // --- 3. Construct payload with current valid values ---
      const payload: Partial<Pick<Customer, 'readBatchSize' | 'readDelayMillis' | 'postURL'>> = {};

      // Add readBatchSize if current value is valid
      const currentBatchNum = Number(currentBatchSizeStr);
      if (currentBatchSizeStr !== '' && !isNaN(currentBatchNum) && currentBatchNum >= 2) {
        payload.readBatchSize = currentBatchNum;
      }

      // Add readDelayMillis if current value is valid
      const currentDelayNum = Number(currentDelayStr);
      if (currentDelayStr !== '' && !isNaN(currentDelayNum) && currentDelayNum >= 11) {
        payload.readDelayMillis = currentDelayNum;
      }

      // Add postURL if current value is valid (non-empty)
      if (currentPostURLStr !== '') {
        payload.postURL = currentPostURLStr;
      }

      // --- 4. Call onSave ---
      await onSave(customer.apikey, payload);
      onClose(); // Close after successful save

    } catch (err: any) {
      setError(err?.message || 'Failed to save changes.');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
          <h3>Edit Customer Settings</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="popup-body">
          <div className="form-group">
            <label>Customer Name</label>
            <input type="text" value={customer.name} disabled />
          </div>

          <div className="form-group">
            <label>API Key</label>
            <input type="text" value={customer.apikey} disabled />
          </div>

          <div className="form-group">
            <label>Read Batch Size</label>
            <input
              type="number"
              value={changes.readBatchSize}
              onChange={handleChange('readBatchSize')}
              placeholder="Leave empty for global setting"
              min="2"
            />
            <small>Must be greater than 1</small>
          </div>

          <div className="form-group">
            <label>Read Delay (ms)</label>
            <input
              type="number"
              value={changes.readDelayMillis}
              onChange={handleChange('readDelayMillis')}
              placeholder="Leave empty for global setting"
              min="11"
            />
            <small>Must be greater than 10 milliseconds</small>
          </div>

          <div className="form-group">
            <label>Post URL</label>
            <input
              type="text"
              value={changes.postURL}
              onChange={handleChange('postURL')}
              placeholder="Leave empty for global setting"
            />
          </div>

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="popup-footer">
          <button className="button secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="button primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}; 